/**
 * Maps Person Search UI state ↔ `POST /api/v1/members/search` request/response.
 */

import { normalizeDigits } from 'modules/string-utils'

/** Page size for members search (initial + each "Load more"). */
export const MEMBERS_SEARCH_PAGE_SIZE = 10

const MEMBERS_SEARCH_SORT_FIELD_BY_COL_ID = Object.freeze({
  lastName: 'lastName',
  firstName: 'firstName',
  companyEmployeeId: 'companyEmployeeId',
  relationship: 'relationship',
  employmentStatus: 'employmentStatus',
  benefitsEffectiveDate: 'benefitsEffectiveDate',
  postalCode: 'postalCode',
  ratingArea: 'ratingArea',
})

/**
 * @param {unknown} filterValue — e.g. react-select option `{ value: 'true' }`
 * @returns {boolean | undefined}
 */
export const parseOptionalBooleanFilter = (filterValue) => {
  if (
    filterValue &&
    !Array.isArray(filterValue) &&
    typeof filterValue === 'object' &&
    Object.prototype.hasOwnProperty.call(filterValue, 'value')
  ) {
    return filterValue.value === 'true'
  }
  return undefined
}

/**
 * @template T
 * @param {Array<{ value: T }> | undefined | null} options
 * @returns {T[] | undefined}
 */
export const mapSelectOptionsToValues = (options) => {
  if (!options?.length) return undefined
  const values = options
    .map((item) =>
      typeof item?.value === 'string' ? item.value.trim() : item?.value
    )
    .filter((value) => value !== '' && value !== undefined)
  return values.length ? values : undefined
}

const EMPLOYMENT_STATUS_REST_VALUE_MAP = Object.freeze({
  FULL_TIME: 'Full-Time',
  PART_TIME: 'Part-Time',
  RETIRED: 'Retired',
  TERMINATED: 'Terminated',
  TERMINATED_NO_COBRA: 'Terminated No Cobra',
  LEAVE: 'Leave',
  NON_PAID_LEAVE_OF_ABSENCE: 'Non-paid LOA',
})

const mapEmploymentStatusOptionsToRestValues = (options) => {
  const values = mapSelectOptionsToValues(options)
  if (!values?.length) return undefined
  return values.map((value) => EMPLOYMENT_STATUS_REST_VALUE_MAP[value] ?? value)
}

const pickDefinedFilterFields = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== undefined && v !== null && v !== '' && v !== 'NoFilter'
    )
  )

/**
 * Maps sidebar `activeFilters` to `SearchUserFilterInputDto` fields (no criteria-row fields).
 * Criteria rows are merged separately so they override when present.
 *
 * @param {Record<string, unknown>} activeFilters
 */
const mapActiveFiltersToSidebarSearchFilters = (activeFilters) => {
  const isExempt = parseOptionalBooleanFilter(activeFilters?.isExempt)
  const isTestAccount = parseOptionalBooleanFilter(activeFilters?.isTestUser)

  const startDate =
    activeFilters.startDate !== '' ? activeFilters.startDate : undefined
  const endDate =
    activeFilters.endDate !== '' ? activeFilters.endDate : undefined
  const benefitsEffectiveDate =
    startDate || endDate ? { startDate, endDate } : undefined

  return {
    cobraStatus: mapSelectOptionsToValues(activeFilters?.cobraStatus),
    enrollmeStatus: mapSelectOptionsToValues(activeFilters?.enrollmeStatus),
    employmentStatus: mapEmploymentStatusOptionsToRestValues(
      activeFilters?.employmentStatus
    ),
    relationshipTypes: mapSelectOptionsToValues(
      activeFilters?.relationshipTypes
    ),
    benefitsEffectiveDate,
    clientPublicKey: activeFilters?.clientPublicKey,
    isExempt,
    isTestAccount,
  }
}

/**
 * Maps structured search criteria rows → `SearchUserFilterInputDto` fields from the form.
 *
 * Name fields: rows are applied in array order. `firstName` / `lastName` always set their
 * keys; `nameOrdered` only supplies `firstNameContains` / `lastNameContains` when those
 * keys are still unset, so a dedicated first/last row does not get overwritten by a
 * combined-name row that appears earlier.
 *
 * @param {unknown[]} rows
 * @returns {Record<string, unknown>}
 */
export const mapSearchCriteriaRowsToRestFilters = (rows) => {
  const safeRows = Array.isArray(rows) ? rows : []
  const rest = {}

  for (const row of safeRows) {
    const field = row?.field
    const raw = String(row?.value ?? '').trim()
    if (!field || raw === '') continue

    if (field === 'firstName') {
      rest.firstNameContains = raw
      continue
    }

    if (field === 'lastName') {
      rest.lastNameContains = raw
      continue
    }

    if (field === 'nameOrdered') {
      const [firstToken, ...restTokens] = raw.split(/\s+/)
      if (firstToken && rest.firstNameContains === undefined) {
        rest.firstNameContains = firstToken
      }
      if (restTokens.length && rest.lastNameContains === undefined) {
        rest.lastNameContains = restTokens.join(' ')
      }
      continue
    }

    if (field === 'state') {
      const code = raw.toUpperCase()
      if (code.length === 2) rest.stateProvince = code
      continue
    }

    if (field === 'zip') {
      rest.postalCodeContains = raw.replace(/\s/g, '')
      continue
    }

    if (field === 'ssnLast4') {
      const ssnLast4 = normalizeDigits(raw)
      if (ssnLast4.length === 4) rest.ssnLastFour = ssnLast4
      continue
    }

    if (field === 'ssn') {
      const ssnDigits = normalizeDigits(raw)
      if (ssnDigits.length === 9) rest.ssnNineDigits = ssnDigits
      continue
    }

    if (field === 'employeeId') {
      rest.companyEmployeeIdContains = raw
      continue
    }

    if (field === 'phone') {
      const phoneDigits = normalizeDigits(raw)
      if (phoneDigits) rest.phoneNumber = phoneDigits
      continue
    }

    if (field === 'email') {
      rest.emailAddressContains = raw
      continue
    }

    if (field === 'employeePublicKey') {
      rest.employeePublicKey = raw
      continue
    }

    if (field === 'personPublicKey') {
      rest.personPublicKey = raw
      continue
    }

    if (field === 'benefitId') {
      rest.benefitId = raw
      continue
    }
  }

  return rest
}

/**
 * Request body for `POST /api/v1/members/search` (`SearchUsersRequestDto`).
 * Single entry: maps `activeFilters` + `searchCriteriaRows` → REST body.
 *
 * @param {Record<string, unknown>} activeFilters
 * @param {unknown[]} searchCriteriaRows
 * @param {{ offset?: number, limit?: number }} [pagination] — new search: offset 0; load more: offset = loaded row count
 */
export const buildMembersSearchRestBody = (
  activeFilters,
  searchCriteriaRows,
  pagination = {},
  gridSortModel = []
) => {
  const sidebarFilters = mapActiveFiltersToSidebarSearchFilters(activeFilters)
  const criteriaRestFilters =
    mapSearchCriteriaRowsToRestFilters(searchCriteriaRows)

  const filtersMerged = pickDefinedFilterFields({
    ...sidebarFilters,
    ...criteriaRestFilters,
  })

  const filters = Object.keys(filtersMerged).length > 0 ? filtersMerged : {}

  const offset = pagination.offset ?? 0
  const limit = pagination.limit ?? MEMBERS_SEARCH_PAGE_SIZE

  const restBody = {
    filters,
    pagination: {
      offset,
      limit,
    },
  }

  const sort = Array.isArray(gridSortModel)
    ? gridSortModel
        .map((item) => {
          const field = MEMBERS_SEARCH_SORT_FIELD_BY_COL_ID[item?.colId]
          const order = item?.sort
          if (!field || (order !== 'asc' && order !== 'desc')) return null
          return { field, order }
        })
        .filter(Boolean)
    : []

  if (sort.length) {
    restBody.sort = sort
  }

  return restBody
}

const toPositiveInt = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value !== '') {
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return undefined
}

/**
 * Extract list + total from `POST /api/v1/members/search` JSON body.
 *
 * Supported shapes (in order):
 * - `{ searchUsers: { rows, total } }` — enrollment-platform / GraphQL-style envelope
 * - `{ rows, total }` — flat REST body when the server omits `searchUsers`
 *
 * @param {unknown} data
 * @returns {{ rows: unknown[], total: number }}
 */
export const normalizeMembersSearchResponse = (data) => {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) {
    return { rows: [], total: 0 }
  }
  const su = data.searchUsers
  if (su != null && typeof su === 'object' && !Array.isArray(su)) {
    const rows = Array.isArray(su.rows) ? su.rows : []
    const total = toPositiveInt(su.total) ?? rows.length
    return { rows, total }
  }
  if (Array.isArray(data.rows)) {
    const rows = data.rows
    const total = toPositiveInt(data.total) ?? rows.length
    return { rows, total }
  }
  return { rows: [], total: 0 }
}
